import { Link } from 'react-router-dom';
import { FileText, Users, Calendar } from 'lucide-react';

interface Activity {
  id: string;
  type: 'note' | 'client';
  title: string;
  subtitle: string;
  timestamp: string;
  link: string;
}

interface TimelineItem {
  id: string;
  type: 'note' | 'client';
  title: string;
  subtitle: string;
  timestamp: string;
  date: Date;
  link: string;
}

interface RecentActivityProps {
  activities: Activity[];
}

export function RecentActivity({ activities }: RecentActivityProps) {
  const formatDateHeader = (date: Date) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const itemDate = new Date(date);
    itemDate.setHours(0, 0, 0, 0);
    const diffTime = now.getTime() - itemDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else {
      return itemDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: itemDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  };

  // Convert activities to timeline items
  const timelineItems: TimelineItem[] = activities.map(activity => ({
    ...activity,
    date: new Date(activity.timestamp),
  }));

  // Sort by date (newest first)
  timelineItems.sort((a, b) => b.date.getTime() - a.date.getTime());

  // Group by date
  const groupedItems = timelineItems.reduce((acc, item) => {
    const dateKey = formatDateHeader(item.date);
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(item);
    return acc;
  }, {} as Record<string, TimelineItem[]>);

  if (timelineItems.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Recent Activity</h3>
        <p className="text-gray-600 mb-4">Start by creating your first clinical note.</p>
        <Link
          to="/app/notes"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create Note
        </Link>
      </div>
    );
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'note':
        return <FileText className="w-5 h-5 text-blue-600" />;
      case 'client':
        return <Users className="w-5 h-5 text-blue-600" />;
      default:
        return <FileText className="w-5 h-5 text-blue-600" />;
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Activity Timeline</h2>
      <div className="bg-white rounded-lg border border-gray-200">
        {Object.entries(groupedItems).map(([dateLabel, items], groupIndex) => (
          <div key={dateLabel} className={groupIndex > 0 ? 'border-t border-gray-200' : ''}>
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700">{dateLabel}</span>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {items.map((item) => (
                <Link
                  key={item.id}
                  to={item.link}
                  className="block p-4 hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50 group-hover:bg-blue-100 transition-colors">
                        {getIcon(item.type)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">
                          {item.type === 'note' ? '📝' : '👤'}
                        </span>
                        <p className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                          {item.title}
                        </p>
                      </div>
                      <p className="text-sm text-gray-600">{item.subtitle}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
